import {
  MaterialPhysics,
  MaterialPhysicsOverride,
  MaterialPhysicsProperty,
  MaterialPhysicsSetRead,
  SupportedLanguage,
  UnitType,
} from "store/api/generatedApi";
import { parseNumber } from "src/utils/parseNumber";

export const PHYSICS_DECIMAL_PLACES = 4;

export const PHYSICS_FIELDS: {
  field: MaterialPhysicsProperty;
  label: string;
  units: UnitType | null;
}[] = [
  { field: "density", label: "density", units: "density" },
  { field: "yield_percent", label: "yield", units: null },
  {
    field: "min_feasible_mass",
    label: "minimumFeasibleMass",
    units: "mass",
  },
  {
    field: "electrical_energy_consumption",
    label: "electricalEnergyConsumption",
    units: "electrical_energy_consumption",
  },
];

export const editablePhysics = (
  physics: MaterialPhysicsOverride
): MaterialPhysicsOverride => ({
  material_id: physics.material_id,
  density: physics.density,
  yield_percent: physics.yield_percent,
  min_feasible_mass: physics.min_feasible_mass,
  electrical_energy_consumption: physics.electrical_energy_consumption,
});

export const editablePhysicsEqual = (
  first: MaterialPhysicsOverride | undefined,
  second: MaterialPhysicsOverride | undefined
): boolean =>
  first !== undefined &&
  second !== undefined &&
  PHYSICS_FIELDS.every(({ field }) => first[field] === second[field]);

export const physicsMatchesDefaults = ({
  physics,
  defaults,
}: {
  physics: Map<number, MaterialPhysicsOverride>;
  defaults: MaterialPhysics[];
}): boolean =>
  physics.size === defaults.length &&
  defaults.every((material) =>
    editablePhysicsEqual(physics.get(material.material_id), material)
  );

export const physicsHasChanged = (
  source: MaterialPhysicsSetRead,
  override: MaterialPhysicsOverride
): boolean => {
  const original = source.items.find(
    (material) => material.material_id === override.material_id
  );
  return original !== undefined && !editablePhysicsEqual(original, override);
};

export const parsePhysicsProperty = ({
  text,
  language,
  field,
}: {
  text: string;
  language: SupportedLanguage;
  field: MaterialPhysicsProperty;
}): number => {
  const parsed = parseNumber(language)({
    decimalPlaces: PHYSICS_DECIMAL_PLACES,
    minimum: 0,
    maximum: field === "yield_percent" ? 100 : undefined,
  })(text);
  if (
    !Number.isFinite(parsed) ||
    ((field === "yield_percent" || field === "density") && parsed <= 0)
  )
    throw new Error("Invalid material physics property");
  return parsed;
};

export const replacePhysicsOverride = ({
  overrides,
  physics,
}: {
  overrides: MaterialPhysicsOverride[];
  physics: MaterialPhysics | MaterialPhysicsOverride;
}): MaterialPhysicsOverride[] => [
  ...overrides.filter(
    (material) => material.material_id !== physics.material_id
  ),
  editablePhysics(physics),
];
